import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { useStore } from 'react-redux';
import { SelectInput, TextInput } from '../../../../components/form-elements/_index';
import { Table } from '../../../../components/tables/Table';
import { FlyoutSaveCancel } from '../../../../components/flyout-components/FlyoutSaveCancel';
import { getPortOfEntrySelectList } from '../../../../selectors/utility-selectors';
import { StatusCode } from '../../../../utils/api';
import { AppUtils } from '../../../../utils/app-utils';
import { ShipmentActionType } from '../../../models/enums/shipment-action-type';
import { DeliveryShipmentAction, ShipmentAction } from '../../../models/shipments/shipment-action';
import { TruckCustomsEntry, UpdateTruckCustomsRequest } from '../../../models/shipments/shipment-update-customs';
import { PortOfEntryReference, TruckRead } from '../../../models/trucks/truck-read';
import { TransportationSvc } from '../../../service';
import { ShipmentActionCustomsRow } from '../ShipmentActionCustomsRow';
import { ShipmentType } from '../../../models/enums/shipment-type';

interface IProps {
    visible: boolean;
    onSuccess: any;
    onClose: any;
    truck: TruckRead|undefined;
}

export const ActionSetTruckCustoms = (props: IProps) => {

    const COUNTRY_CODE_CANADA = "CA";
    const store = useStore();
    const [service] = React.useState(new TransportationSvc());
    const [editTruck, setEditTruck] = React.useState(new TruckRead());
    const [massRename, setMassRename] = React.useState('');

    const [portsOfEntry] = React.useState(() => {
        return getPortOfEntrySelectList(store.getState());
    });
    /**
     * Copy the truck to local state for editing
     */
    React.useEffect(() => {
        if (props.visible && props.truck) {
            const tmpTruck = {...props.truck};

            tmpTruck.actions = tmpTruck.actions.filter((itm : ShipmentAction) => {
                return ((itm.type === ShipmentActionType.Delivery) && 
                        (itm.address.country === COUNTRY_CODE_CANADA));
            });

            setEditTruck(tmpTruck);
        }
    }, [props.visible]);

    /**
     * Handle the PARS number changing
     * @param stopId The stop ID we're editing
     * @param value The new value to set
     */
    const onPARSChange = (stopId : number, value : any) => {

        const tmpTruck = {...editTruck} as TruckRead;
        tmpTruck.actions
            .filter((stop : ShipmentAction) => {
                return stop.type === ShipmentActionType.Delivery;
            })
            .forEach((itm : DeliveryShipmentAction) => {
                if (itm.id === stopId) {
                    itm.parsNumber = value.target.value;
                }
            });

        setEditTruck(tmpTruck);
    };

    const onPortOfEntryChange = (e : any) => {

        const tmpTruck = {...editTruck} as TruckRead;
        if (!tmpTruck.portOfEntry) {
            tmpTruck.portOfEntry = new PortOfEntryReference();
        }

        tmpTruck.portOfEntry.id = Number.parseInt(e.target.value, 10);
        setEditTruck(tmpTruck);
    }; 

    const onMassRenameChange = (e : any) => {
        setMassRename(e.target.value.toUpperCase());
    };

    const onUpdateAllPARS = () => {

        if (massRename.length > 0 && massRename.indexOf('PARS') > -1) {

            const allParts = massRename.split('PARS');
            if (allParts.length !== 2) {
                return;
            }

            const labelString = allParts[0];
            if (labelString.length === 0) {
                return;
            }

            const numberString = allParts[1];
            let numericPart = Number.parseInt(numberString, 10);
            if (isNaN(numericPart)) {
                return;
            }
            
            const tmpTruck = {...editTruck} as TruckRead;
            tmpTruck.actions.forEach((itm : DeliveryShipmentAction) => {
                itm.parsNumber = `${ labelString }PARS${ numericPart.toString().padStart(numberString.length, '0') }`;
                numericPart += 1;
            });

            setEditTruck(tmpTruck);
            setMassRename('');
        }
    };

    /**
     * Save the customs info back to the server, and if successful
     * close the flyout
     */
    const onSave = async () => {
        
        const request       = new UpdateTruckCustomsRequest();
        request.rowVersion  = props.truck!.rowVersion;
        request.portofEntryId = editTruck.portOfEntry ? editTruck.portOfEntry.id : undefined;
        
        request.stops = editTruck.actions
            .filter((stop : ShipmentAction) => {
                return stop.type === ShipmentActionType.Delivery;
            })
            .map((itm : DeliveryShipmentAction) => {
                return new TruckCustomsEntry(itm.id, itm.parsNumber);
            });
        
        const result = await service.setCustomsInfo(
            request,
            props.truck!.id,
            ShipmentType.Truck
        );

        if (result.status === StatusCode.Success) {
            setEditTruck(undefined as unknown as TruckRead);
            props.onSuccess();
        }
    };

    /**
     * Cleanup our state and close
     */
    const cleanup = () => {
        setEditTruck(undefined as unknown as TruckRead);
        props.onClose();
    };

    return <Drawer
            anchor="right"
            onClose={cleanup}
            open={props.visible}>
            {
                !AppUtils.IsEmpty(editTruck)
                    ? <div className="stop-action-drawer stop-action-reorder-route">

                        <div className="row">
                            <div className="col-md-12">
                                <h1>Set Customs Information</h1>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-md-6">
                                <SelectInput
                                    id="portOfEntry"
                                    label="Port of Entry"
                                    disable={ editTruck.actions.length === 0 }
                                    onChange={ onPortOfEntryChange }
                                    options={ portsOfEntry }
                                    value={ editTruck.portOfEntry?.id }
                                />
                            </div>

                            <div className="col-md-6">
                                <div className="form-group row text-btn-combo">
                                    <TextInput
                                        type="text"
                                        id="massName"
                                        disable={ editTruck.actions.length === 0 }
                                        className="col-md-9"
                                        label="Set All PARS Number"
                                        onChange={ onMassRenameChange }
                                        value={ massRename }
                                    />
                                    <button className="btn action col-md-3" onClick={ onUpdateAllPARS } disabled={ massRename.length < 1 } >Set All</button>
                                </div>
                            </div>
                        </div>
                        
                        <div className="row">
                            <Table
                                tableClass="table-striped"
                                noResultsMessage="There are no Canadian stops for this truck yet."
                                transparentBackground={true}
                                numberOfActions={ 1 }
                                header={[
                                    'Address', 
                                    'Dealer', 
                                    'PARS Number'
                                ]}>
                                {                            
                                    editTruck.actions.length > 0
                                        ? editTruck.actions
                                            .filter((stop : ShipmentAction) => {
                                                return stop.type === ShipmentActionType.Delivery;
                                            })
                                            .map((itm: DeliveryShipmentAction) =>
                                                <ShipmentActionCustomsRow
                                                    key={ itm.id }
                                                    delivery={itm}
                                                    onPARSChange={ onPARSChange }
                                                />
                                            )
                                        : null
                                }
                            </Table>
                        </div>

                        <FlyoutSaveCancel
                            onSave={ onSave }
                            onCancel={ cleanup }
                        />

                    </div>
                    : null
                }
        </Drawer>;
};
